import XLSX from 'xlsx';
import { isUntrustedValue } from '../tools/sanitize';
import type { IGpthelpExportMeta } from './gpthelp-export-registry';

export type IGpthelpExcelColumnType = 'string' | 'number' | 'date' | 'datetime';

export interface IGpthelpExcelColumn {
  key: string;
  header: string;
  type?: IGpthelpExcelColumnType;
}

export interface IGpthelpExcelSheetInput {
  name: string;
  columns: IGpthelpExcelColumn[];
  rows: Array<Record<string, unknown>>;
}

/**
 * Which text cells get a leading apostrophe so a spreadsheet never reads
 * them as a formula: `strict` (the default) every text starting with = + - @,
 * for data the model writes (it can hold customer-written text);
 * `storeText` only = and @, for the store's own catalog text, where
 * «+ Bacon» and «- 2 empanadas» are common bullets.
 */
export type IGpthelpExcelFormulaGuard = 'strict' | 'storeText';

export interface IGpthelpExcelBuildInput {
  filename: string;
  sheets: IGpthelpExcelSheetInput[];
  /** Defaults to `strict`. */
  formulaGuard?: IGpthelpExcelFormulaGuard;
}

export interface IGpthelpExcelBuildOutput {
  filename: string;
  mime: string;
  bytes: Uint8Array;
  meta: IGpthelpExportMeta;
}

/** Rows per sheet a workbook may hold. */
export const EXCEL_MAX_ROWS = 20_000;

const LIMITS = {
  maxSheets: 5,
  maxColumns: 50,
  maxRows: EXCEL_MAX_ROWS,
  maxCellStringLen: 5_000,
};

/**
 * The sheets cut to `maxRows` rows each, and whether any was cut, for an
 * export built from the store's own data (it never fails for its size).
 */
export function capSheetRows(
  sheets: IGpthelpExcelSheetInput[],
  maxRows: number = EXCEL_MAX_ROWS
): { sheets: IGpthelpExcelSheetInput[]; truncated: boolean } {
  return {
    sheets: sheets.map((sheet) =>
      sheet.rows.length > maxRows
        ? { ...sheet, rows: sheet.rows.slice(0, maxRows) }
        : sheet
    ),
    truncated: sheets.some((sheet) => sheet.rows.length > maxRows),
  };
}

const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const INVALID_SHEET_CHARS_RE = /[:\\/?*\[\]]/g;
const INVALID_FILENAME_CHARS_RE = /[<>:"/\\|?*\u0000-\u001F]/g;

/** The first characters that get the apostrophe, per guard (leading spaces ignored). */
const FORMULA_PREFIX_RE: Record<IGpthelpExcelFormulaGuard, RegExp> = {
  strict: /^[=+\-@]/,
  storeText: /^[=@]/,
};

function sanitizeSheetName(input: string, fallbackIndex: number): string {
  const base = (input ?? '').trim() || `Sheet${fallbackIndex}`;
  const cleaned = base.replace(INVALID_SHEET_CHARS_RE, ' ').trim();
  const truncated = cleaned.length > 31 ? cleaned.slice(0, 31).trim() : cleaned;
  return truncated || `Sheet${fallbackIndex}`;
}

function ensureXlsxFilename(input: string): string {
  const raw = (input ?? '').trim() || 'export';
  const cleaned = raw.replace(INVALID_FILENAME_CHARS_RE, ' ').trim();
  const base = cleaned || 'export';
  return base.toLowerCase().endsWith('.xlsx') ? base : `${base}.xlsx`;
}

function clampString(value: string): string {
  if (value.length <= LIMITS.maxCellStringLen) return value;
  return value.slice(0, LIMITS.maxCellStringLen);
}

function sanitizeCellValue(params: {
  value: unknown;
  type?: IGpthelpExcelColumnType;
  formulaGuard: IGpthelpExcelFormulaGuard;
}): string | number | null {
  // A customer text the model copied from a read result: export the text.
  const value = isUntrustedValue(params.value)
    ? params.value.untrusted
    : params.value;
  const { type } = params;
  if (value === null || typeof value === 'undefined') return null;

  if (type === 'number') {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : null;
  }

  // For date/datetime we export as a safe ISO string (no locale assumptions).
  if (type === 'date' || type === 'datetime') {
    if (value instanceof Date && !isNaN(value.getTime())) {
      const iso = value.toISOString();
      return type === 'date' ? iso.slice(0, 10) : iso;
    }
    if (typeof value === 'string') {
      const s = value.trim();
      const parsed = new Date(s);
      if (!isNaN(parsed.getTime())) {
        const iso = parsed.toISOString();
        return type === 'date' ? iso.slice(0, 10) : iso;
      }
      return clampString(s);
    }
    return clampString(String(value));
  }

  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'boolean') return value ? 'true' : 'false';

  const s = clampString(String(value));
  const trimmedStart = s.trimStart();
  // Formula injection protection (Excel/Sheets)
  if (FORMULA_PREFIX_RE[params.formulaGuard].test(trimmedStart)) {
    return `'${s}`;
  }
  return s;
}

export function buildExcelExport(input: IGpthelpExcelBuildInput): IGpthelpExcelBuildOutput {
  if (!input || typeof input !== 'object') {
    throw new Error('Invalid export input');
  }

  const filename = ensureXlsxFilename(input.filename);
  const formulaGuard = input.formulaGuard ?? 'strict';

  if (!Array.isArray(input.sheets) || input.sheets.length === 0) {
    throw new Error('At least one sheet is required');
  }
  if (input.sheets.length > LIMITS.maxSheets) {
    throw new Error(`Too many sheets (max ${LIMITS.maxSheets})`);
  }

  const wb = XLSX.utils.book_new();
  const metaSheets: IGpthelpExportMeta['sheets'] = [];

  input.sheets.forEach((sheet, idx) => {
    if (!sheet || typeof sheet !== 'object') {
      throw new Error(`Invalid sheet at index ${idx}`);
    }
    if (!Array.isArray(sheet.columns) || sheet.columns.length === 0) {
      throw new Error(`Sheet "${sheet.name}" must have columns`);
    }
    if (sheet.columns.length > LIMITS.maxColumns) {
      throw new Error(`Too many columns in sheet "${sheet.name}" (max ${LIMITS.maxColumns})`);
    }
    if (!Array.isArray(sheet.rows)) {
      throw new Error(`Sheet "${sheet.name}" rows must be an array`);
    }
    if (sheet.rows.length > LIMITS.maxRows) {
      throw new Error(`Too many rows in sheet "${sheet.name}" (max ${LIMITS.maxRows})`);
    }

    const sheetName = sanitizeSheetName(sheet.name, idx + 1);

    const headers = sheet.columns.map((c) => (c.header ?? c.key ?? '').toString());
    const aoa: unknown[][] = [headers];

    for (const row of sheet.rows) {
      const r: unknown[] = sheet.columns.map((col) =>
        sanitizeCellValue({
          value: (row ?? {})[col.key],
          type: col.type,
          formulaGuard,
        }),
      );
      aoa.push(r);
    }

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    metaSheets.push({
      name: sheetName,
      rows: sheet.rows.length,
      columns: sheet.columns.length,
    });
  });

  const raw: unknown = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const bytes = (() => {
    if (raw instanceof Uint8Array) return raw;
    if (raw instanceof ArrayBuffer) return new Uint8Array(raw);
    if (Array.isArray(raw) && raw.every((v) => typeof v === 'number')) {
      return new Uint8Array(raw);
    }
    throw new Error('Failed to serialize workbook');
  })();

  return {
    filename,
    mime: XLSX_MIME,
    bytes,
    meta: {
      kind: 'xlsx',
      sheetCount: metaSheets.length,
      sheets: metaSheets,
    },
  };
}

